/**
 * Observes the measured width of an element and returns it as React state.
 *
 * Lets components adapt their layout to whatever container they're rendered
 * in — sidebars, embed slots, narrow drawers — without knowing or caring
 * what's hosting them. The ResizeObserver fires whenever the element's
 * border-box changes, so flex / grid resizing is picked up naturally.
 *
 * @module hooks/use-container-width
 */

import { useEffect, useRef, useState, type RefObject } from "react";

/**
 * Returns the live measured width (in CSS pixels) of the element behind the
 * given ref. Returns 0 until the first observation lands so callers can
 * branch with `width > 0 && width < threshold` to skip the initial frame.
 *
 * The ref OBJECT is stable across renders, so an effect keyed on `[ref]` would
 * run only once — at first mount. That breaks when the measured element only
 * appears on a *later* render: e.g. the chat mounts in its centered empty
 * state (where the measured container doesn't exist yet, so `ref.current` is
 * null), then swaps to the message layout after the first send. A one-shot
 * effect saw `null` and never re-attaches, so width stays 0 forever and any
 * `isNarrow`-style branch silently picks the wrong layout until a full reload.
 * Instead we poll `ref.current` after every render (a cheap identity compare)
 * and (re)attach the observer whenever the underlying node actually changes.
 */
export function useContainerWidth(ref: RefObject<HTMLElement | null>): number {
  const [width, setWidth] = useState(0);
  const observedRef = useRef<HTMLElement | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  // No dependency array: runs after every render, but the body short-circuits
  // unless the observed node changed, so the steady-state cost is one ref read.
  useEffect(() => {
    const el = ref.current;
    if (el === observedRef.current) return;

    observedRef.current = el;
    observerRef.current?.disconnect();
    observerRef.current = null;

    if (!el) {
      setWidth(0);
      return;
    }

    // Seed from the current rect so consumers don't render at width=0 for an
    // extra frame when the element was already laid out before mount.
    setWidth(el.getBoundingClientRect().width);

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      // borderBoxSize is the modern API; fall back to contentRect for older
      // browsers (and for older WebKit builds where borderBoxSize is missing).
      const next =
        entry.borderBoxSize?.[0]?.inlineSize ?? entry.contentRect.width;
      setWidth(next);
    });

    observer.observe(el);
    observerRef.current = observer;
  });

  // Disconnect only on unmount — not on every render — so the observer set up
  // above survives the no-deps effect re-running.
  useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      observedRef.current = null;
    };
  }, []);

  return width;
}
